import { useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from "react";

import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { colorForCharacterFromMap } from "@/lib/timeline/character/colors";
import { locationRunsForMount } from "@/lib/timeline/location/LocationMarker";
import { locationBoxColors, locationColorIndex } from "@/lib/timeline/location/LocationMarker/palette";
import type { PlaybackState } from "@/lib/timeline/playback";
import {
  applyLayoutTimelineOverlays,
  buildTimelineLayoutFrame,
} from "@/lib/timeline/scene/TimelineFrame";
import type { TimelineViewTransform } from "@/lib/timeline/view/ViewTransform/transform";
import { timelineFrameDetail } from "./timelineFrameDetail";
import { TimelinePovIndicatorLayer } from "./TimelinePovIndicatorLayer";

interface LayoutTimelineOverlaysProps {
  canvas: HTMLCanvasElement | null;
  layout: BookTimelineLayout;
  getPlayback: () => PlaybackState;
  getViewportWidth: () => number;
  getViewportHeight: () => number;
  getViewTransform: () => TimelineViewTransform;
}

export function LayoutTimelineOverlays({
  canvas,
  layout,
  getPlayback,
  getViewportWidth,
  getViewportHeight,
  getViewTransform,
}: LayoutTimelineOverlaysProps) {
  const characterRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const mountRuns = useMemo(() => locationRunsForMount(layout), [layout]);

  useEffect(() => {
    if (canvas === null || characterRef.current === null || locationRef.current === null) {
      return;
    }
    const characterRoot = characterRef.current;
    const locationRoot = locationRef.current;

    const sync = (event: Event): void => {
      if (timelineFrameDetail(event)?.boardSample !== undefined) {
        return;
      }
      const playback = getPlayback();
      applyLayoutTimelineOverlays(
        characterRoot,
        locationRoot,
        layout,
        buildTimelineLayoutFrame(
          playback,
          getViewportWidth(),
          getViewportHeight(),
        ),
        getViewTransform(),
      );
    };

    canvas.addEventListener("timeline-frame", sync);
    sync(new CustomEvent("timeline-frame", { detail: { playback: getPlayback() } }));
    return () => canvas.removeEventListener("timeline-frame", sync);
  }, [
    canvas,
    getPlayback,
    getViewTransform,
    getViewportHeight,
    getViewportWidth,
    layout,
  ]);

  return (
    <>
      <div ref={characterRef} className="timeline-labels" aria-hidden="true">
        <div className="timeline-marker-layer timeline-marker-layer--discs">
          {layout.markers.map((marker) => (
            <div
              key={marker.characterId}
              data-marker-part="disc"
              data-character-id={marker.characterId}
              className="timeline-character-marker"
            >
              <div
                className="timeline-marker__disc"
                style={{
                  backgroundColor: colorForCharacterFromMap(
                    marker.characterId,
                    layout.characterColors,
                  ),
                }}
              >
                <div className="timeline-marker__face" data-face="from" />
                <div className="timeline-marker__face" data-face="to" />
              </div>
            </div>
          ))}
        </div>
        <TimelinePovIndicatorLayer />
        <div className="timeline-marker-layer timeline-marker-layer--names">
          {layout.markers.map((marker) => (
            <span
              key={marker.characterId}
              data-marker-part="name"
              data-character-id={marker.characterId}
              className="timeline-marker__name"
            >
              {marker.displayName}
            </span>
          ))}
        </div>
      </div>
      <div ref={locationRef} className="timeline-locations" aria-hidden="true">
        {mountRuns.map((run, index) => (
          locationStack(run, locationColorIndex(mountRuns, index))
        ))}
      </div>
    </>
  );
}

function locationStack(
  run: ReturnType<typeof locationRunsForMount>[number],
  index: number,
): ReactNode {
  const colors = locationBoxColors(index);
  return (
    <div
      key={run.runId}
      data-location-run={run.runId}
      className="timeline-location-stack"
      style={
        {
          "--location-bg": colors.background,
          "--location-border": colors.border,
          "--location-text": colors.text,
        } as CSSProperties
      }
    >
      <div className="timeline-location-box">
        <span className="timeline-location-box__label">{run.locationLabel}</span>
      </div>
      <div className="timeline-location-parts" data-location-parts="" />
    </div>
  );
}
